import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const EXPIRY_MS = 15 * 60 * 1000;

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const [supportCode, setSupportCode] = useState(null);     // { code, expiresAt }
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  // Countdown ticker
  useEffect(() => {
    if (!supportCode) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(supportCode.expiresAt) - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        setSupportCode(null);
        clearInterval(timerRef.current);
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [supportCode]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const handleGetSupportCode = async () => {
    setCodeLoading(true);
    setCodeError('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch('/api/support-token/generate', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        navigate('/login');
        return;
      }
      if (!res.ok) throw new Error('Failed to generate support code');
      const data = await res.json();
      setSupportCode(data);
    } catch (err) {
      setCodeError('Could not generate a support code. Please try again.');
    } finally {
      setCodeLoading(false);
    }
  };

  const handleCopy = () => {
    if (supportCode?.code) {
      navigator.clipboard.writeText(supportCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const mins = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const secs = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div className="dashboard-layout" style={{ position: 'relative', minHeight: '100vh' }}>
      <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="logout-btn" onClick={handleGetSupportCode} disabled={codeLoading} title="Get a one-time code to verify your identity with support">
          {codeLoading ? 'Generating...' : 'Get Support Code'}
        </button>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      {supportCode && (
        <div style={{
          position: 'fixed', top: 70, right: 20, zIndex: 100,
          background: '#1e2a1e', border: '1px solid #2A4233', borderRadius: 8,
          padding: '16px 20px', minWidth: 260, boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
        }}>
          <div style={{ fontSize: 12, color: '#a0b0a0', marginBottom: 6 }}>Support Verification Code</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 28, letterSpacing: 4, color: '#7fff7f', fontWeight: 700 }}>
              {supportCode.code}
            </span>
            <button onClick={handleCopy} style={{ fontSize: 11, padding: '3px 8px', cursor: 'pointer' }}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: secondsLeft < 60 ? '#ff6b6b' : '#a0b0a0', marginTop: 6 }}>
            Expires in {mins}:{secs}
          </div>
          <div style={{ fontSize: 11, color: '#6a806a', marginTop: 4 }}>
            Share this code with a support agent to verify your identity. One-time use only.
          </div>
          <button onClick={() => setSupportCode(null)} style={{ marginTop: 10, fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      )}

      {codeError && (
        <div style={{ position: 'fixed', top: 70, right: 20, zIndex: 100, background: '#3a1a1a', border: '1px solid #7f2020', borderRadius: 8, padding: '12px 16px', color: '#ff8080', fontSize: 13 }}>
          {codeError}
          <button onClick={() => setCodeError('')} style={{ marginLeft: 10, fontSize: 11, cursor: 'pointer' }}>✕</button>
        </div>
      )}

      <div style={{ paddingTop: 60 }}>
        {children}
      </div>
    </div>
  );
};

export default DashboardLayout;
